import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { 
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Rating, 
  Chip, 
  Button, 
  Stack, 
  IconButton, 
  TextField,
  useTheme,
  useMediaQuery,
  Alert,
  LinearProgress,
  Fab,
  SwipeableDrawer,
  AppBar,
  Toolbar,
  Stepper,
  Step,
  StepLabel,
  Collapse,
  Paper,
  Drawer
} from '@mui/material';
import styled from '@emotion/styled';
import { 
  PhotoCamera, 
  Add, 
  Info, 
  LocationOn, 
  Category, 
  Clear, 
  ExpandMore, 
  SmartToy,
  Upgrade,
  CheckCircle,
  Close as CloseIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Menu as MenuIcon,
  Star as StarIcon,
  Business as BusinessIcon,
  Assistant as AssistantIcon,
  ExpandLess
} from '@mui/icons-material';

import { useSpReviewPostMutation } from '../../redux/thunks/sp/spreviewpost';
import { useGenerateTextMutation } from '../../redux/thunks/ai/generatetext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetUserProfileQuery } from '../../redux/thunks/users/userprofile';

// Utility function to get current month key
const getCurrentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}`; // Add 1 since getMonth() is 0-based
};

// AI Quota Constants
const AI_QUOTAS = {
  review_generation: {
    free: process.env.REACT_APP_BUILD_ENV === "prod" ? 50 : 3,
    premium: Infinity // unlimited
  }
};

// Styled components using @emotion/styled
const ResponsiveLayout = styled(Box)`
  display: flex;
  gap: ${props => props.theme.spacing(2)};
  padding: ${props => props.theme.spacing(2)};
  
  ${props => props.theme.breakpoints.down('md')} {
    flex-direction: column;
    padding: 0;
  }
`;

const BusinessImage = styled('img')`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: ${props => props.theme.shape.borderRadius}px;
  margin-bottom: ${props => props.theme.spacing(2)};
  
  ${props => props.theme.breakpoints.down('md')} {
    height: 150px;
    margin-bottom: ${props => props.theme.spacing(1)};
  }
`;

const TipsList = styled(Box)`
  max-height: 300px;
  overflow-y: auto;
  padding-right: ${props => props.theme.spacing(1)};
  
  '&::-webkit-scrollbar': {
    width: 6px,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: ${props => props.theme.palette.grey[300]},
    borderRadius: 3,
  }
`;

const LeftColumnContainer = styled(Stack)`
  gap: ${props => props.theme.spacing(2)};
  width: 300px;
  
  ${props => props.theme.breakpoints.down('md')} {
    width: 100%;
  }
`;

const ColumnCard = styled(Card)`
  background-color: ${props => props.theme.palette.background.paper};
  border-radius: ${props => props.theme.shape.borderRadius}px;
  box-shadow: ${props => props.theme.shadows[0]};
  
  ${props => props.theme.breakpoints.down('md')} {
    width: 100% !important;
    margin-bottom: ${props => props.theme.spacing(1)};
    border-radius: 0;
    box-shadow: none;
    border: none;
  }
`;

const ReviewTextArea = styled('textarea')`
  width: 100%;
  min-height: 200px;
  padding: ${props => props.theme.spacing(2)};
  border: 1px solid ${props => props.theme.palette.divider};
  border-radius: ${props => props.theme.shape.borderRadius}px;
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: ${props => props.theme.typography.body1.fontSize};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.palette.primary.main};
  }
  
  ${props => props.theme.breakpoints.down('sm')} {
    min-height: 120px;
    padding: ${props => props.theme.spacing(1.5)};
  }
`;

const PhotoPreviewGrid = styled(Stack)`
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing(1)};
`;

const PhotoPreviewContainer = styled(Box)`
  position: relative;
  width: 100px;
  height: 100px;
  
  ${props => props.theme.breakpoints.down('sm')} {
    width: 80px;
    height: 80px;
  }
`;

const PhotoPreview = styled('img')`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
`;

const DeleteButton = styled(IconButton)`
  position: absolute;
  top: -8px;
  right: -8px;
  padding: 4px;
  background-color: ${props => props.theme.palette.error.main};
  color: white;
  
  &:hover {
    background-color: ${props => props.theme.palette.error.dark};
  }
`;

const MainContainer = styled(Box)`
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;
  padding: ${props => props.theme.spacing(2)};
  
  ${props => props.theme.breakpoints.down('md')} {
    padding: 0;
    min-height: 100vh;
  }
`;

const AiInputSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(2)};
`;

// 🆕 Mobile Step Progress Component
const MobileStepProgress = ({ currentStep, rating, reviewText }) => {
  const steps = [
    'Business & Rating',
    'Write & Submit Review'
  ];

  const getStepProgress = (step) => {
    switch (step) {
      case 0: return rating !== null;
      case 1: return reviewText.length > 10;
      default: return false;
    }
  };

  return (
    <Paper 
      sx={{ 
        p: 2, 
        mb: 1, 
        bgcolor: 'primary.light',
        position: 'sticky',
        top: '48px',
        zIndex: 100
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 2, color: 'primary.contrastText' }}>
        Write Your Review
      </Typography>
      <Stepper activeStep={currentStep} orientation="horizontal" sx={{ mb: 1 }}>
        {steps.map((label, index) => (
          <Step key={label} completed={getStepProgress(index)}>
            <StepLabel 
              sx={{
                '& .MuiStepLabel-label': {
                  color: 'primary.contrastText',
                  fontSize: '0.75rem'
                }
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      <Typography variant="caption" sx={{ color: 'primary.contrastText', opacity: 0.8 }}>
        Step {currentStep + 1} of {steps.length}: {steps[currentStep]}
      </Typography>
    </Paper>
  );
};

// 🆕 Mobile Info Sidebar Component
const MobileInfoSidebar = ({ 
  isOpen, 
  onClose,
  feedbackProps,
  selectedPositive,
  selectedKeywords,
  canUseAI,
  userAccountType,
  currentAIUsage,
  aiQuotaLimit,
  isUserLoading
}) => {
  const [expandedSections, setExpandedSections] = useState({
    business: true,
    tips: false,
    ai: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={isOpen}
      onClose={onClose}
      onOpen={() => {}}
      PaperProps={{
        sx: { 
          height: '80vh',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          bgcolor: 'background.default'
        }
      }}
    >
      {/* Drag Handle */}
      <Box
        sx={{
          width: 40,
          height: 4,
          bgcolor: 'grey.300',
          borderRadius: 2,
          mx: 'auto',
          mt: 1,
          mb: 2
        }}
      />

      <Box sx={{ px: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Review Information
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ overflow: 'auto', height: 'calc(80vh - 80px)' }}>
          {/* Business Info Section */}
          <Card sx={{ mb: 2 }}>
            <CardContent 
              sx={{ 
                pb: 1, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={() => toggleSection('business')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Business Details</Typography>
              </Box>
              {expandedSections.business ? <ExpandLess /> : <ExpandMore />}
            </CardContent>
            
            <Collapse in={expandedSections.business}>
              <CardContent sx={{ pt: 0 }}>
                <BusinessImage
                  src={feedbackProps.skillPhoto}
                  alt={feedbackProps?.skill?.businessName || 'Business'}
                />
                
                <Typography variant="subtitle1" gutterBottom>
                  {feedbackProps?.skill?.businessName}
                </Typography>
                
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Category fontSize="small" color="action" />
                    <Typography color="textSecondary" variant="body2">
                      {feedbackProps?.skill?.category[0]}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography color="textSecondary" variant="body2">
                      {feedbackProps?.skill?.geocodeResults?.formattedAddress || 'Location'}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Collapse>
          </Card>

          {/* Writing Tips Section */}
          <Card sx={{ mb: 2 }}>
            <CardContent 
              sx={{ 
                pb: 1, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={() => toggleSection('tips')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Info sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Writing Tips</Typography>
              </Box>
              {expandedSections.tips ? <ExpandLess /> : <ExpandMore />}
            </CardContent>
            
            <Collapse in={expandedSections.tips}>
              <CardContent sx={{ pt: 0 }}>
                <TipsList>
                  {WRITING_TIPS.map((tip, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        mb: 1.5,
                        '&:last-child': { mb: 0 }
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        color="textSecondary"
                        sx={{ 
                          display: 'flex',
                          alignItems: 'flex-start' 
                        }}
                      >
                        <span style={{ marginRight: '8px' }}>•</span>
                        {tip}
                      </Typography>
                    </Box>
                  ))}
                </TipsList>
              </CardContent>
            </Collapse>
          </Card>

          {/* AI Assistant Section */}
          <Card sx={{ mb: 2 }}>
            <CardContent 
              sx={{ 
                pb: 1, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={() => toggleSection('ai')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AssistantIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">AI Status</Typography>
              </Box>
              {expandedSections.ai ? <ExpandLess /> : <ExpandMore />}
            </CardContent>
            
            <Collapse in={expandedSections.ai}>
              <CardContent sx={{ pt: 0 }}>
                <AIQuotaDisplay 
                  userAccountType={userAccountType}
                  currentUsage={currentAIUsage}
                  quotaLimit={aiQuotaLimit}
                  isLoading={isUserLoading}
                />
                
                {selectedPositive.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      Selected Positives:
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {selectedPositive.join(', ')}
                    </Typography>
                  </Box>
                )}
                
                {selectedKeywords.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" gutterBottom>
                      Selected Keywords:
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {selectedKeywords.join(', ')}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Collapse>
          </Card>
        </Box>
      </Box>
    </SwipeableDrawer>
  );
};

// AI Quota Display Component
const AIQuotaDisplay = ({ userAccountType, currentUsage, quotaLimit, isLoading }) => {
  if (isLoading) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="textSecondary">
          Loading AI usage...
        </Typography>
      </Box>
    );
  }

  const usagePercentage = quotaLimit === Infinity ? 0 : (currentUsage / quotaLimit) * 100;
  const canUseAI = quotaLimit === Infinity || currentUsage < quotaLimit;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="caption" color="textSecondary">
          AI Reviews Generated
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {quotaLimit === Infinity ? `${currentUsage} (Unlimited)` : `${currentUsage}/${quotaLimit}`}
        </Typography>
      </Box>
      
      {quotaLimit !== Infinity && (
        <Box sx={{ width: '100%', mb: 1 }}>
          <LinearProgress 
            variant="determinate" 
            value={Math.min(usagePercentage, 100)}
            color={canUseAI ? 'primary' : 'error'}
          />
        </Box>
      )}
      
      <Typography 
        variant="caption" 
        color={canUseAI ? 'success.main' : 'error.main'}
      >
        {userAccountType === 'premium' 
          ? 'Unlimited AI generations' 
          : canUseAI 
            ? `${quotaLimit - currentUsage} remaining this month`
            : 'Monthly limit reached'
        }
      </Typography>
    </Box>
  );
};

// AI Limit Exceeded Component
const AILimitExceeded = ({ userAccountType, onUpgrade }) => {
  return (
    <Alert 
      severity="warning" 
      sx={{ mb: 2 }}
      action={
        userAccountType === 'free' ? (
          <Button 
            color="inherit" 
            size="small" 
            startIcon={<Upgrade />}
            onClick={onUpgrade}
          >
            Upgrade
          </Button>
        ) : null
      }
    >
      <Typography variant="body2">
        {userAccountType === 'free' 
          ? 'You\'ve reached your monthly AI review generation limit. Upgrade to Premium for unlimited AI assistance!'
          : 'You\'ve reached your monthly AI generation limit. Please try again next month.'
        }
      </Typography>
      
      {userAccountType === 'free' && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <CheckCircle sx={{ mr: 0.5, fontSize: 14 }} />
            Unlimited AI review generation
          </Typography>
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <CheckCircle sx={{ mr: 0.5, fontSize: 14 }} />
            Advanced AI features
          </Typography>
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircle sx={{ mr: 0.5, fontSize: 14 }} />
            Priority support
          </Typography>
        </Box>
      )}
    </Alert>
  );
};

// Predefined options
const POSITIVE_EXPERIENCES = [
  'Great Service',
  'Quality Products',
  'Clean Space',
  'Good Value',
  'Professional',
  'Friendly Staff',
];

// Add writing tips array
const WRITING_TIPS = [
  'Be specific about your experience',
  'Include details about the service or product',
  'Mention what makes this business unique',
  'Share both positive and constructive feedback',
  'Describe the customer service quality',
  'Talk about the value for money',
  'Include details about your visit timing',
  'Comment on the atmosphere or environment'
];

const WritingTipsCard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Desktop Card View
  const TipsContent = () => (
    <TipsList>
      {WRITING_TIPS.map((tip, index) => (
        <Box 
          key={index} 
          sx={{ 
            display: 'flex', 
            gap: 1, 
            mb: 1.5,
            '&:last-child': { mb: 0 }
          }}
        >
          <Typography 
            variant="body2" 
            color="textSecondary"
            sx={{ 
              display: 'flex',
              alignItems: 'flex-start' 
            }}
          >
            <span style={{ marginRight: '8px' }}>•</span>
            {tip}
          </Typography>
        </Box>
      ))}
    </TipsList>
  );

  // Return different layouts based on screen size
  if (isMobile) {
    return (
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: theme.palette.background.paper,
            borderBottom: `0px solid ${theme.palette.divider}`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Info fontSize="small" color="primary" />
            <Typography variant="subtitle1">Writing Tips</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TipsContent />
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <ColumnCard>
      <CardContent>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            mb: 2 
          }}
        >
          <Info fontSize="small" color="primary" />
          Writing Tips
        </Typography>
        <TipsContent />
      </CardContent>
    </ColumnCard>
  );
};

// 🆕 Mobile Step Content Components
const BusinessRatingStep = ({ feedbackProps, rating, setRating }) => (
  <Box sx={{ p: 2 }}>
    <Typography variant="h6" gutterBottom>
      How was your experience?
    </Typography>
    
    {/* Business Info Summary */}
    <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box
          component="img"
          src={feedbackProps.skillPhoto}
          alt={feedbackProps?.skill?.businessName || 'Business'}
          sx={{
            width: 60,
            height: 60,
            borderRadius: 1,
            objectFit: 'cover'
          }}
        />
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            {feedbackProps?.skill?.businessName}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {feedbackProps?.skill?.category[0]}
          </Typography>
        </Box>
      </Box>
    </Paper>

    {/* Rating Section */}
    <Box sx={{ textAlign: 'center', py: 3 }}>
      <Typography variant="h6" gutterBottom>
        Overall Rating
      </Typography>
      <Rating 
        size="large"
        value={rating}
        onChange={(_, newValue) => setRating(newValue)}
        sx={{ fontSize: '3rem' }}
      />
      {rating && (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          {rating === 1 && "Poor"}
          {rating === 2 && "Fair"} 
          {rating === 3 && "Good"}
          {rating === 4 && "Very Good"}
          {rating === 5 && "Excellent"}
        </Typography>
      )}
    </Box>

    {/* Experience Chips */}
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        What did you like? (Optional)
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {POSITIVE_EXPERIENCES.map(exp => (
          <Chip
            key={exp}
            label={exp}
            onClick={() => {/* Handle in parent component */}}
            color="default"
            size="small"
            sx={{ m: 0.5 }}
          />
        ))}
      </Stack>
    </Box>
  </Box>
);

const WriteReviewStep = ({ 
  reviewText, 
  setReviewText, 
  photos, 
  photoUrls, 
  handlePhotoUpload, 
  handleDeletePhoto,
  canUseAI,
  keyword1, setKeyword1, 
  keyword2, setKeyword2, 
  keyword3, setKeyword3,
  handleGenerateReview,
  isgenerateTextLoading,
  userAccountType,
  currentAIUsage,
  aiQuotaLimit,
  isUserLoading,
  selectedPositive
}) => (
  <Box sx={{ p: 2 }}>
    <Typography variant="h6" gutterBottom>
      Write Your Review
    </Typography>
    
    {/* Quick AI Section - Compact */}
    {canUseAI && (
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.lighter' }}>
        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToy fontSize="small" color="primary" />
          Need help? Use AI to draft your review
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            size="small"
            label="Keyword 1"
            value={keyword1}
            onChange={(e) => setKeyword1(e.target.value)}
            placeholder="e.g., service"
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            label="Keyword 2" 
            value={keyword2}
            onChange={(e) => setKeyword2(e.target.value)}
            placeholder="e.g., quality"
            sx={{ flex: 1 }}
          />
        </Box>
        
        <Button
          fullWidth
          variant="contained"
          onClick={handleGenerateReview}
          disabled={isgenerateTextLoading}
          startIcon={<SmartToy />}
          size="small"
        >
          {isgenerateTextLoading ? 'Generating...' : 'Generate with AI'}
        </Button>
        
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
          AI will create a review based on your rating{selectedPositive.length > 0 && ' and selected experiences'}
        </Typography>
      </Paper>
    )}

    {/* Review Text Area */}
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        Your Review
      </Typography>
      <ReviewTextArea
        value={reviewText}
        onChange={(e) => setReviewText(e.target.value)}
        placeholder={canUseAI ? "Write your review here or use AI to generate one..." : "Share details about your experience, the service quality, atmosphere, and what made it special..."}
      />
      <Typography variant="caption" color="textSecondary">
        {reviewText.length} characters {reviewText.length < 10 && '• Need at least 10 to submit'}
        {reviewText.length >= 10 && '• Ready to submit ✓'}
      </Typography>
    </Box>

    {/* Photo Upload Section */}
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Add Photos (Optional)
      </Typography>
      <PhotoPreviewGrid>
        <IconButton
          color="primary"
          component="label"
          sx={{
            width: '80px',
            height: '80px',
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <input
            hidden
            type="file"
            multiple
            accept="image/*"
            onChange={handlePhotoUpload}
          />
          <Add />
        </IconButton>
        
        {photoUrls.map((url, index) => (
          <PhotoPreviewContainer key={index}>
            <PhotoPreview
              src={url}
              alt={`Upload ${index + 1}`}
            />
            <DeleteButton
              size="small"
              onClick={() => handleDeletePhoto(index)}
            >
              <Clear sx={{ fontSize: 16 }} />
            </DeleteButton>
          </PhotoPreviewContainer>
        ))}
      </PhotoPreviewGrid>
      
      {photos.length > 0 && (
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
          {photos.length} photo{photos.length !== 1 ? 's' : ''} selected
        </Typography>
      )}
    </Box>
    
    {/* AI Quota Display - Compact */}
    {canUseAI && (
      <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="caption" color="textSecondary">
          AI Usage: {currentAIUsage}/{aiQuotaLimit === Infinity ? '∞' : aiQuotaLimit} this month
        </Typography>
      </Box>
    )}
  </Box>
);

const AIEnhancementStep = ({ 
  canUseAI, 
  keyword1, setKeyword1, 
  keyword2, setKeyword2, 
  keyword3, setKeyword3,
  handleGenerateReview,
  isgenerateTextLoading,
  isSubmitting,
  rating,
  userAccountType,
  currentAIUsage,
  aiQuotaLimit,
  isUserLoading,
  reviewText,
  setReviewText
}) => (
  <Box sx={{ p: 2 }}>
    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <SmartToy color="primary" />
      AI Assistant
    </Typography>

    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
      {reviewText.length > 0 
        ? "Enhance your existing review with AI or submit as is."
        : "Let AI help you draft a great review based on your rating and keywords."
      }
    </Typography>

    {/* AI Quota Display */}
    <AIQuotaDisplay 
      userAccountType={userAccountType}
      currentUsage={currentAIUsage}
      quotaLimit={aiQuotaLimit}
      isLoading={isUserLoading}
    />

    {!canUseAI ? (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Monthly AI limit reached. {reviewText.length > 0 ? "You can still submit your review manually." : "Please write your review manually."}
        </Typography>
      </Alert>
    ) : (
      <AiInputSection>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Add keywords to help AI {reviewText.length > 0 ? "enhance" : "draft"} your review:
        </Typography>
        
        <TextField
          fullWidth
          size="small"
          label="Keyword 1 (e.g., atmosphere, service)"
          value={keyword1}
          onChange={(e) => setKeyword1(e.target.value)}
          variant="outlined"
          sx={{ mb: 2 }}
        />
        
        <TextField
          fullWidth
          size="small"
          label="Keyword 2 (e.g., quality, value)"
          value={keyword2}
          onChange={(e) => setKeyword2(e.target.value)}
          variant="outlined"
          sx={{ mb: 2 }}
        />
        
        <TextField
          fullWidth
          size="small"
          label="Keyword 3 (e.g., recommend, experience)"
          value={keyword3}
          onChange={(e) => setKeyword3(e.target.value)}
          variant="outlined"
          sx={{ mb: 3 }}
        />

        <Button
          fullWidth
          variant="contained"
          onClick={handleGenerateReview}
          disabled={!rating || isSubmitting || isgenerateTextLoading}
          startIcon={<SmartToy />}
          size="large"
          sx={{ mb: 2 }}
        >
          {isgenerateTextLoading 
            ? 'Generating...' 
            : reviewText.length > 0 
              ? 'Enhance with AI' 
              : 'Generate Review with AI'
          }
        </Button>
        
        <Typography variant="caption" color="textSecondary" sx={{ textAlign: 'center', display: 'block' }}>
          AI will {reviewText.length > 0 ? "enhance your review" : "create a review"} based on your {rating}-star rating and keywords.
        </Typography>
      </AiInputSection>
    )}

    {/* Review Editor - Always show, even if empty */}
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        {reviewText.length > 0 ? "Your Review:" : "Review Text:"}
      </Typography>
      <TextField
        fullWidth
        multiline
        rows={6}
        value={reviewText}
        onChange={(e) => setReviewText(e.target.value)}
        variant="outlined"
        placeholder={reviewText.length > 0 ? "Edit your review here..." : "Your AI-generated review will appear here, or write your own..."}
        sx={{ mb: 2 }}
      />
      <Typography variant="caption" color="textSecondary">
        {reviewText.length} characters • You can edit this review before submitting
      </Typography>
    </Box>

    {/* Submit requirement notice */}
    {reviewText.length < 10 && (
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          You'll need at least 10 characters in your review to submit.
        </Typography>
      </Alert>
    )}
  </Box>
);

const WriteReview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const userId = useSelector((state) => state.auth?.userId || null);

  const feedbackProps = location.state;
  console.log('in write review feedbackProps: ', feedbackProps);  
  
  // Redux state and mutations
  const [createReview, { isLoading: isReviewLoading }] = useSpReviewPostMutation();
  const [generateText, { isLoading: isgenerateTextLoading }] = useGenerateTextMutation();

  const { data: userData, isError: userProfileError, isLoading: isUserLoading, error } = useGetUserProfileQuery(userId);
  console.log('userData', userData);

  // AI Quota Logic
  const userAccountType = userData?.accountDetails?.type || 'free';
  const aiQuotaLimit = AI_QUOTAS.review_generation[userAccountType] || AI_QUOTAS.review_generation.free;
  
  // Get current month's AI usage
  const currentMonthKey = getCurrentMonthKey();
  const reviewGenerationUsage = userData?.quotaUsage?.ai_review_generation?.monthly || {};
  const currentAIUsage = reviewGenerationUsage[currentMonthKey] || 0;
  
  const canUseAI = aiQuotaLimit === Infinity || currentAIUsage < aiQuotaLimit;

  const handleUpgradeClick = () => {
    navigate('/premium');
  };

  // 🆕 Mobile state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentMobileStep, setCurrentMobileStep] = useState(0);

  // Add state for AI keywords
  const [keyword1, setKeyword1] = useState('');
  const [keyword2, setKeyword2] = useState('');
  const [keyword3, setKeyword3] = useState('');

  // Local state
  const [rating, setRating] = useState(null);
  const [reviewText, setReviewText] = useState('');
  const [selectedPositive, setSelectedPositive] = useState([]);
  const [selectedImprovement, setSelectedImprovement] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [photoUrls, setPhotoUrls] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const token = useSelector((state) => state.auth.token);

  // 🆕 Mobile step management - Only 2 steps now
  const getMobileStepFromProgress = () => {
    if (rating === null) return 0;
    return 1; // Go directly to combined write/AI step
  };

  React.useEffect(() => {
    if (isMobile) {
      const newStep = getMobileStepFromProgress();
      if (newStep > currentMobileStep && currentMobileStep === 0) {
        setCurrentMobileStep(newStep);
      }
    }
  }, [rating, isMobile]);

  const handleGenerateReview = async () => {
    // Check AI quota before generation
    if (!canUseAI) {
      return;
    }

    let auth = {
      token: token,
      authType: 'token',
    };
    const payload = {
      auth,
      businessId: feedbackProps.businessId,
      keywords: [
        ...selectedKeywords,
        keyword1,
        keyword2,
        keyword3
      ].filter(Boolean), // Remove empty strings
      
      generationType: 'review',
    };

    console.log('API Payload:', payload);

    try {
      const generatedReview = await generateText({payload});
      console.log('generatedReview', generatedReview);
      setReviewText(generatedReview.data.text);
    } catch (error) {
      console.error('Error generating review:', error);
    }
  };

  // Handle chip selection
  const handleChipClick = (chip, type) => {
    switch (type) {
      case 'positive':
        setSelectedPositive(prev => 
          prev.includes(chip) ? prev.filter(item => item !== chip) : [...prev, chip]
        );
        break;
      case 'improvement':
        setSelectedImprovement(prev => 
          prev.includes(chip) ? prev.filter(item => item !== chip) : [...prev, chip]
        );
        break;
      case 'keyword':
        setSelectedKeywords(prev => 
          prev.includes(chip) ? prev.filter(item => item !== chip) : [...prev, chip]
        );
        break;
    }
  };

  // Handle photo upload
  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    // Create preview URLs for the new files
    const newPhotoUrls = files.map(file => URL.createObjectURL(file));
    
    setPhotos(prevPhotos => [...prevPhotos, ...files]);
    setPhotoUrls(prevUrls => [...prevUrls, ...newPhotoUrls]);
  };

  // Handle photo deletion
  const handleDeletePhoto = (index) => {
    // Revoke the object URL to avoid memory leaks
    URL.revokeObjectURL(photoUrls[index]);
    
    setPhotos(prevPhotos => prevPhotos.filter((_, i) => i !== index));
    setPhotoUrls(prevUrls => prevUrls.filter((_, i) => i !== index));
  };

  // Handle review submission
  const handleSubmit = async () => {
    if (!rating) return;

    setIsSubmitting(true);
    try {
      let auth = {
        token: token,
        authType: 'token',
      };
      let review = {
        review: reviewText,
        rating: rating,
        skillId: feedbackProps.skillId,
        businessId: feedbackProps.businessId
      };

      const payload = {
        auth,
        review,
        spId: feedbackProps.spId,
        positiveExperiences: selectedPositive,
        improvementAreas: selectedImprovement,
        keywords: selectedKeywords,
        photos: photoUrls,
      };
      
      await createReview({payload }).unwrap();
      
      // After successful submission, navigate back to the business details page
      navigate(`/business/${feedbackProps.businessId}/${feedbackProps.skill.businessName.replace(/[\s,\.]+/g, '-').toLowerCase()}`, { 
        state: { 
          reviewSubmitted: true
        } 
      });
    } catch (error) {
      console.error('Error submitting review:', error);
      navigate(`/business/${feedbackProps.businessId}/${feedbackProps.skill.businessName.replace(/[\s,\.]+/g, '-').toLowerCase()}`, { 
        state: { 
          message: {
            type: 'error',
            title: 'Unable to Submit Review',
            description: 'There was a problem submitting your review. Please try again later.'
          }
        } 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // 🆕 Mobile step content rendering - Only 2 steps
  const renderMobileStepContent = () => {
    switch (currentMobileStep) {
      case 0:
        return (
          <BusinessRatingStep
            feedbackProps={feedbackProps}
            rating={rating}
            setRating={setRating}
          />
        );
      case 1:
        return (
          <WriteReviewStep
            reviewText={reviewText}
            setReviewText={setReviewText}
            photos={photos}
            photoUrls={photoUrls}
            handlePhotoUpload={handlePhotoUpload}
            handleDeletePhoto={handleDeletePhoto}
            canUseAI={canUseAI}
            keyword1={keyword1}
            setKeyword1={setKeyword1}
            keyword2={keyword2}
            setKeyword2={setKeyword2}
            keyword3={keyword3}
            setKeyword3={setKeyword3}
            handleGenerateReview={handleGenerateReview}
            isgenerateTextLoading={isgenerateTextLoading}
            userAccountType={userAccountType}
            currentAIUsage={currentAIUsage}
            aiQuotaLimit={aiQuotaLimit}
            isUserLoading={isUserLoading}
            selectedPositive={selectedPositive}
          />
        );
      default:
        return null;
    }
  };

  const handleMobileNext = () => {
    if (currentMobileStep === 0 && rating) {
      setCurrentMobileStep(1);
    }
  };

  const handleMobileBack = () => {
    if (currentMobileStep > 0) {
      setCurrentMobileStep(prev => prev - 1);
    }
  };

  // Clean up URLs when component unmounts
  React.useEffect(() => {
    return () => {
      photoUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: "#f5f5f5",
        minHeight: '100vh',
        pt: isMobile ? '48px' : 0,
        pb: isMobile ? 0 : 2
      }}
    >
      {isMobile ? (
        // 🆕 Mobile Layout
        <>
          {/* Mobile Step Progress */}
          <MobileStepProgress 
            currentStep={currentMobileStep}
            rating={rating}
            reviewText={reviewText}
          />

          {/* AI Limit Exceeded Alert - Full Width */}
          {!canUseAI && (
            <Box sx={{ p: 1 }}>
              <AILimitExceeded 
                userAccountType={userAccountType}
                onUpgrade={handleUpgradeClick}
              />
            </Box>
          )}

          {/* Mobile Step Content */}
          <Paper 
            variant="outlined" 
            sx={{ 
              border: 'none', 
              borderRadius: 0,
              minHeight: 'calc(100vh - 160px)'
            }}
          >
            {renderMobileStepContent()}

            {/* Mobile Navigation Buttons - Simplified for 2 steps */}
            <Box sx={{ 
              p: 2, 
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex', 
              justifyContent: 'space-between',
              gap: 2,
              bgcolor: 'background.paper'
            }}>
              <Button
                onClick={handleMobileBack}
                disabled={currentMobileStep === 0}
                size="large"
                sx={{ minWidth: 100 }}
              >
                Back
              </Button>
              
              {currentMobileStep === 0 ? (
                <Button
                  variant="contained"
                  onClick={handleMobileNext}
                  disabled={!rating}
                  size="large"
                  sx={{ minWidth: 120 }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={!rating || reviewText.length < 10 || isSubmitting}
                  size="large"
                  sx={{ minWidth: 120 }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Review'}
                </Button>
              )}
            </Box>
          </Paper>

          {/* Mobile Floating Action Button */}
          <Fab
            color="primary"
            sx={{ 
              position: 'fixed', 
              bottom: 100, // Above bottom navigation
              right: 16,
              zIndex: 1000
            }}
            onClick={() => setSidebarOpen(true)}
          >
            <Info />
          </Fab>

          {/* Mobile Info Sidebar */}
          <MobileInfoSidebar
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            feedbackProps={feedbackProps}
            selectedPositive={selectedPositive}
            selectedKeywords={selectedKeywords}
            canUseAI={canUseAI}
            userAccountType={userAccountType}
            currentAIUsage={currentAIUsage}
            aiQuotaLimit={aiQuotaLimit}
            isUserLoading={isUserLoading}
          />
        </>
      ) : (
        // 🆕 Desktop Layout - Original (unchanged)
        <MainContainer>
          {/* AI Limit Exceeded Alert - Full Width */}
          {!canUseAI && (
            <AILimitExceeded 
              userAccountType={userAccountType}
              onUpgrade={handleUpgradeClick}
            />
          )}

          <ResponsiveLayout>
            {/* Business Info Card */}
            <LeftColumnContainer>
              {/* Business Info Card */}
              <ColumnCard>
                <CardContent>
                  <BusinessImage
                    src={feedbackProps.skillPhoto}
                    alt={feedbackProps?.skill?.businessName || 'Business'}
                  />
                  
                  <Typography variant="subtitle" gutterBottom>
                    {feedbackProps?.skill?.businessName}
                  </Typography>
                  
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Category fontSize="small" color="action" />
                      <Typography color="textSecondary" variant="body2">
                        {feedbackProps?.skill?.category[0]}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOn fontSize="small" color="action" />
                      <Typography color="textSecondary" variant="body2">
                        {feedbackProps?.skill?.geocodeResults?.formattedAddress || 'Location'}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Mobile-only Rating Section */}
                  <Box sx={{ display: { md: 'none' }, mt: 2 }}>
                    <Typography gutterBottom>Overall Rating</Typography>
                    <Rating 
                      size="large"
                      value={rating}
                      onChange={(_, newValue) => setRating(newValue)}
                    />
                  </Box>
                </CardContent>
              </ColumnCard>

              {/* Writing Tips - Now using the new component */}
              <WritingTipsCard />
            </LeftColumnContainer>

            {/* Main Review Content */}
            <Box sx={{ flex: 1 }}>
              <ColumnCard>
                <CardContent>
                  {/* Desktop-only Rating Section */}
                  <Box sx={{ display: { xs: 'none', md: 'block' }, mb: 3 }}>
                    <Typography gutterBottom>Overall Rating</Typography>
                    <Rating 
                      size="large"
                      value={rating}
                      onChange={(_, newValue) => setRating(newValue)}
                    />
                  </Box>

                  {/* Experience Chips */}
                  <Box sx={{ mb: 3 }}>
                    <Typography gutterBottom>What did you like?</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {POSITIVE_EXPERIENCES.map(exp => (
                        <Chip
                          key={exp}
                          label={exp}
                          onClick={() => handleChipClick(exp, 'positive')}
                          color={selectedPositive.includes(exp) ? 'primary' : 'default'}
                          sx={{ m: 0.5 }}
                        />
                      ))}
                    </Stack>
                  </Box>

                  {/* Review Text */}
                  <Box sx={{ mb: 3 }}>
                    <Typography gutterBottom>Write Your Review</Typography>
                    <ReviewTextArea
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      placeholder="Share your experience..."
                    />
                  </Box>

                  {/* Photo Upload Section */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Add Photos
                    </Typography>
                    <PhotoPreviewGrid>
                      <IconButton
                        color="primary"
                        component="label"
                        sx={{
                          width: { xs: '80px', sm: '100px' },
                          height: { xs: '80px', sm: '100px' },
                          border: '2px dashed',
                          borderColor: 'divider',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <input
                          hidden
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handlePhotoUpload}
                        />
                        <Add />
                      </IconButton>
                      
                      {photoUrls.map((url, index) => (
                        <PhotoPreviewContainer key={index}>
                          <PhotoPreview
                            src={url}
                            alt={`Upload ${index + 1}`}
                          />
                          <DeleteButton
                            size="small"
                            onClick={() => handleDeletePhoto(index)}
                          >
                            <Clear sx={{ fontSize: 16 }} />
                          </DeleteButton>
                        </PhotoPreviewContainer>
                      ))}
                    </PhotoPreviewGrid>
                    
                    {photos.length > 0 && (
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                        {photos.length} photo{photos.length !== 1 ? 's' : ''} selected
                      </Typography>
                    )}
                  </Box>

                  {/* Submit Button */}
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={!rating || isSubmitting}
                    onClick={handleSubmit}
                    sx={{ mt: 2 }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Review'}
                  </Button>
                </CardContent>
              </ColumnCard>
            </Box>

            {/* AI Assistant - Desktop */}
            <ColumnCard sx={{ width: 300, display: { xs: 'none', md: 'block' } }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SmartToy color="primary" />
                  AI Assistant
                </Typography>

                {/* AI Quota Display */}
                <AIQuotaDisplay 
                  userAccountType={userAccountType}
                  currentUsage={currentAIUsage}
                  quotaLimit={aiQuotaLimit}
                  isLoading={isUserLoading}
                />

                {/* Compact limit indicator for narrow column */}
                {!canUseAI && (
                  <Alert severity="warning" sx={{ mb: 2, py: 1 }}>
                    <Typography variant="caption">
                      Monthly AI limit reached
                    </Typography>
                  </Alert>
                )}

                <AiInputSection>
                  <Typography variant="body2" color="textSecondary">
                    Add additional keywords to enhance your review:
                  </Typography>
                  
                  <TextField
                    fullWidth
                    size="small"
                    label="Keyword 1"
                    value={keyword1}
                    onChange={(e) => setKeyword1(e.target.value)}
                    variant="outlined"
                    disabled={!canUseAI}
                  />
                  
                  <TextField
                    fullWidth
                    size="small"
                    label="Keyword 2"
                    value={keyword2}
                    onChange={(e) => setKeyword2(e.target.value)}
                    variant="outlined"
                    disabled={!canUseAI}
                  />
                  
                  <TextField
                    fullWidth
                    size="small"
                    label="Keyword 3"
                    value={keyword3}
                    onChange={(e) => setKeyword3(e.target.value)}
                    variant="outlined"
                    disabled={!canUseAI}
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleGenerateReview}
                    disabled={!rating || isSubmitting || !canUseAI || isgenerateTextLoading}
                    startIcon={<SmartToy />}
                  >
                    {isgenerateTextLoading ? 'Generating...' : 'Generate Review'}
                  </Button>

                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    Selected items:
                  </Typography>
                  
                  <Box sx={{ mt: 1 }}>
                    {selectedPositive.length > 0 && (
                      <Typography variant="body2">
                        Positives: {selectedPositive.join(', ')}
                      </Typography>
                    )}
                    
                    {selectedImprovement.length > 0 && (
                      <Typography variant="body2">
                        Improvements: {selectedImprovement.join(', ')}
                      </Typography>
                    )}
                    
                    {selectedKeywords.length > 0 && (
                      <Typography variant="body2">
                        Keywords: {selectedKeywords.join(', ')}
                      </Typography>
                    )}
                  </Box>
                </AiInputSection>
              </CardContent>
            </ColumnCard>
          </ResponsiveLayout>
        </MainContainer>
      )}
    </Box>
  );
};

export default WriteReview;